import { useScienceTutorSection } from './scienceTutorContext';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { formatModelNumber } from './modelInputContract';
import { HEATING_FIELDS, HEATING_MODEL_MAX_C, type HeatingResult, type HeatingSetup } from './waterHeatingCalc';

/** Four learner inputs for the heating explorer. Invalid input keeps the last valid setup. */
export function HeatingSetupPanel({ setup, onChange }: { setup: HeatingSetup; onChange: (next: HeatingSetup) => void }) {
  useScienceTutorSection('HeatingSetupPanel', { setup });
  return (
    <ModelInputPanel
      legend="Set up your experiment"
      fields={HEATING_FIELDS}
      values={setup}
      onChange={(next) => onChange(next as HeatingSetup)}
      sliderKey="timeS"
    />
  );
}

/** The worked heating calculation: energy in, then each stage with the learner's numbers substituted. */
export function HeatingWorkingPanel({ result, massG }: { result: HeatingResult; massG: number }) {
  const { state } = result;
  const parts = [
    [state.fractions.solid * massG, 'g ice'],
    [state.fractions.liquid * massG, 'g liquid water'],
    [state.fractions.gas * massG, 'g steam'],
  ]
    .filter(([grams]) => (grams as number) > 0.05)
    .map(([grams, label]) => `${formatModelNumber(grams as number)} ${label as string}`);
  return (
    <WorkedStepsPanel
      title="How the model works it out"
      lead={result.energyWorking}
      leadTestId="energy-working"
      steps={result.steps.map((step) => ({
        id: step.phase,
        title: step.title,
        formula: step.formula,
        working: step.working,
        status: step.status,
        note:
          step.status === 'not-reached'
            ? `needs ${formatModelNumber(step.requiredJ, 0)} J`
            : `${formatModelNumber(step.usedJ, 0)} of ${formatModelNumber(step.requiredJ, 0)} J`,
      }))}
      stepTestIdPrefix="heating-step"
      result={`Result: ${formatModelNumber(state.temperatureC)} °C · ${parts.join(' + ')}`}
      resultTestId="heating-result"
      limitNote={
        result.beyondModel
          ? `${formatModelNumber(result.energyJ - result.totalModelJ, 0)} J more than the model needs to reach ${HEATING_MODEL_MAX_C} °C. The model stops there instead of guessing.`
          : null
      }
    />
  );
}
